import { clsx } from 'clsx'
import { useEffectOnce } from 'hook/useEffectOnce'
import { useLatestCallback } from 'hook/useLatestCallback'
import { CSSProperties, ReactElement, ReactNode, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { styles } from 'util/style'
import css from './Tooltip.module.scss'

type Props = {
  children: ReactNode
  tooltip: string | ReactElement
}

function touch() {
  try {
    document.createEvent('TouchEvent')
    return 'touchstart'
  } catch {
    return 'mousedown'
  }
}

export function Tooltip(props: Props) {
  const { children } = props
  const text = typeof props.tooltip === 'string'
  const refOrigin = useRef<HTMLDivElement>(null)
  const refTooltip = useRef<HTMLDivElement>(null)
  const refTimeout = useRef<TimeoutId>()
  const [style, setStyle] = useState<CSSProperties>()
  const [show, setShow] = useState(false)
  const [open, setOpenRaw] = useState(false)

  const setOpen = useLatestCallback((value: boolean) => {
    if (open === value) return

    setShow(false)
    clearTimeout(refTimeout.current)

    if (value) setOpenRaw(true)
    else refTimeout.current = setTimeout(() => setOpenRaw(false), 100)
  })

  const onClickOutside = useLatestCallback((event: MouseEvent | TouchEvent) => {
    const tooltip = refTooltip.current
    const target = event.target as Node
    if (open && tooltip && !tooltip.contains(target)) setOpen(false)
  })

  const onScroll = useLatestCallback(() => {
    setOpen(false)
  })

  useEffectOnce(() => {
    const type = touch()

    document.addEventListener(type, onClickOutside)
    document.addEventListener('scroll', onScroll)

    return () => {
      document.removeEventListener(type, onClickOutside)
      document.removeEventListener('scroll', onScroll)
    }
  })

  const updatePosition = useLatestCallback(() => {
    const origin = refOrigin.current
    const tooltip = refTooltip.current
    if (!origin || !tooltip) return

    const point = origin.getBoundingClientRect()
    const size = tooltip.getBoundingClientRect()
    const x = point.left + point.width / 2
    const y = point.top
    const ax = x
    const ay = y - 8 - 4
    let left = x - size.width / 2
    let top = y - size.height - 8 - 4
    left = Math.max(16, left)
    top = Math.max(16, top)

    const style = styles({
      left,
      top,
      '--arrow-x': ax + 'px',
      '--arrow-y': ay + 'px',
    })

    setShow(true)
    setStyle(style)
  })

  useEffect(() => {
    if (open) updatePosition()
  }, [open, updatePosition])

  const onClick = useLatestCallback(() => {
    if (!open) setOpen(true)
  })

  const className = clsx(css.tooltip, css.top, text && css.default, show && css.show)

  const tooltip = open && <div ref={refTooltip} className={className} style={style}>
    {props.tooltip}
  </div>

  return <div ref={refOrigin} className={css.origin} onClick={onClick}>
    {children}
    {tooltip && createPortal(tooltip, document.body)}
  </div>
}
